import * as React from "react";
import { makeStyles, Text, Button } from "@fluentui/react-components";
import { Copy16Regular, ArrowReply16Regular } from "@fluentui/react-icons";
import CardAccent from "../shared/CardAccent";
import { copyToClipboard, openReplyForm } from "../../../services/outlookActions";

const useStyles = makeStyles({
  card: {
    backgroundColor: "var(--bg-elevated, #FFFFFF)",
    border: "1px solid var(--border-primary, #E0E2E6)",
    borderRadius: "12px",
    boxShadow: "var(--shadow-sm, 0 1px 2px rgba(11,17,26,0.06))",
    overflow: "hidden",
    transition: "box-shadow 0.2s ease, transform 0.2s ease",
    ":hover": {
      boxShadow: "var(--shadow-md, 0 4px 8px rgba(11,17,26,0.08))",
      transform: "translateY(-1px)",
    },
  },
  content: {
    padding: "16px",
  },
  title: {
    fontSize: "14px",
    fontWeight: "600",
    color: "var(--text-primary, #0B111A)",
    marginBottom: "12px",
    display: "block",
  },
  body: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  paragraph: {
    fontSize: "13px",
    lineHeight: "1.55",
    color: "var(--text-secondary, #4A4F57)",
    margin: "0",
  },
  bullet: {
    fontSize: "13px",
    lineHeight: "1.55",
    color: "var(--text-secondary, #4A4F57)",
    margin: "0",
    paddingLeft: "16px",
    position: "relative",
    "::before": {
      content: '"•"',
      position: "absolute",
      left: "4px",
      color: "#FFB500",
      fontWeight: "700",
    },
  },
  bold: {
    fontWeight: "600",
    color: "var(--text-primary, #0B111A)",
  },
  actions: {
    display: "flex",
    gap: "8px",
    marginTop: "14px",
    paddingTop: "12px",
    borderTop: "1px solid var(--border-primary, #E0E2E6)",
  },
  copyBtn: {
    minWidth: "auto",
    fontSize: "12px",
  },
  insertBtn: {
    minWidth: "auto",
    fontSize: "12px",
    backgroundColor: "#FFB500",
    color: "#0B111A",
    fontWeight: "600",
    border: "none",
    ":hover": {
      backgroundColor: "#E6A300",
    },
    ":active": {
      backgroundColor: "#CC9200",
    },
  },
  copiedText: {
    fontSize: "11px",
    color: "var(--color-success, #7ED956)",
    alignSelf: "center",
    marginLeft: "4px",
  },
});

/**
 * Render markdown-like body text: supports paragraphs, bold (**text**), and bullet lines (- text).
 */
function renderMarkdown(markdown, styles) {
  if (!markdown) return null;

  const lines = markdown.split("\n");

  return lines.map((line, idx) => {
    const trimmed = line.trim();
    if (!trimmed) return null;

    // Bullet line
    if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
      const bulletText = trimmed.slice(2);
      return (
        <p key={idx} className={styles.bullet}>
          {renderInlineBold(bulletText, styles)}
        </p>
      );
    }

    // Normal paragraph
    return (
      <p key={idx} className={styles.paragraph}>
        {renderInlineBold(trimmed, styles)}
      </p>
    );
  });
}

/**
 * Replace **bold** segments with <strong> elements.
 */
function renderInlineBold(text, styles) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={i} className={styles.bold}>
          {part.slice(2, -2)}
        </strong>
      );
    }
    return part;
  });
}

/**
 * Extract plain text from markdown for clipboard.
 */
function stripMarkdown(markdown) {
  if (!markdown) return "";
  return markdown.replace(/\*\*/g, "");
}

/**
 * SummaryCard — executive summary with markdown body, copy, and insert-into-reply.
 *
 * @param {{ card: { title: string, body_markdown: string, actions: Array } }} props
 */
const SummaryCard = ({ card }) => {
  const styles = useStyles();
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    const success = await copyToClipboard(stripMarkdown(card.body_markdown));
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleInsert = () => {
    const htmlBody = (card.body_markdown || "")
      .split("\n")
      .filter((l) => l.trim())
      .map((line) => {
        const trimmed = line.trim();
        if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
          return `<li>${trimmed.slice(2)}</li>`;
        }
        return `<p>${trimmed}</p>`;
      })
      .join("");
    openReplyForm(htmlBody);
  };

  return (
    <div className={styles.card}>
      <CardAccent />
      <div className={styles.content}>
        <Text className={styles.title}>{card.title || "Summary"}</Text>
        <div className={styles.body}>{renderMarkdown(card.body_markdown, styles)}</div>
        <div className={styles.actions}>
          <Button
            className={styles.copyBtn}
            appearance="subtle"
            size="small"
            icon={<Copy16Regular />}
            onClick={handleCopy}
          >
            {copied ? "Copied!" : "Copy"}
          </Button>
          <Button
            className={styles.insertBtn}
            size="small"
            icon={<ArrowReply16Regular />}
            onClick={handleInsert}
          >
            Insert into reply
          </Button>
          {copied && <Text className={styles.copiedText}>✓</Text>}
        </div>
      </div>
    </div>
  );
};

export default SummaryCard;
